import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { transformPurchasesData, calculatePurchasesStats, groupPurchasesByPeriod, groupPurchasesBySupplier, groupPurchasesByProduct } from '../../utils/purchasesDataUtils';
import { useCompanySettings } from '../../contexts/CompanySettingsContext';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import toast from 'react-hot-toast';

const PurchasesReports = ({ onBack, documents, products, suppliers, purchases }) => {
  const [selectedReport, setSelectedReport] = useState('purchase-by-period');
  const [periodFilter, setPeriodFilter] = useState('month');
  const [isGenerating, setIsGenerating] = useState(false);
  const { companySettings } = useCompanySettings();
  
  console.log('🔍 PurchasesReports data received:', {
    purchasesCount: purchases?.length || 0,
    documentsCount: documents?.length || 0,
    productsCount: products?.length || 0,
    suppliersCount: suppliers?.length || 0,
    samplePurchase: purchases?.[0],
    sampleDocument: documents?.[0]
  });
  
  // Types de rapports d'achats
  const reportTypes = [
    {
      id: 'purchase-by-period',
      name: 'Achats par période',
      description: 'Analyse des achats par jour, semaine, mois ou année',
    },
    {
      id: 'purchase-by-supplier',
      name: 'Achats par fournisseur',
      description: 'Répartition des achats par fournisseur',
    },
    {
      id: 'purchase-by-product',
      name: 'Achats par produit',
      description: 'Détail des achats par produit ou catégorie',
    },
  ];

  // Transform purchases data using SHARED utility - ensures consistency with PurchasesPage
  const transformedPurchases = useMemo(() => {
    console.log('🔄 PurchasesReports: Using shared transformation utility');
    
    if (!purchases || purchases.length === 0) {
      console.log('⚠️ No purchases data available for transformation');
      return [];
    }

    // Use the SHARED transformation utility - SINGLE SOURCE OF TRUTH
    return transformPurchasesData(purchases, suppliers);
  }, [purchases, suppliers]);

  // Calculate purchase data for different report types using REAL DATA
  const purchaseData = useMemo(() => {
    console.log('📊 Calculating purchase data from transformed purchases:', transformedPurchases.length);

    if (!transformedPurchases || transformedPurchases.length === 0) {
      return {
        periodPurchases: [],
        supplierPurchases: [],
        productPurchases: [],
        pendingOrders: []
      };
    }

    // Period purchases using REAL data
    const periodPurchases = groupPurchasesByPeriod(transformedPurchases, periodFilter)
      .slice(0, 12) // Last 12 periods
      .map(period => {
        // Get the most common purchase type in this period
        const typeCount = {};
        period.purchases.forEach(purchase => {
          const type = purchase.type || 'bon-commande';
          typeCount[type] = (typeCount[type] || 0) + 1;
        });
        const mostCommonType = Object.keys(typeCount).reduce((a, b) => 
          typeCount[a] > typeCount[b] ? a : b, 'bon-commande'
        );
        
        // Get the most common status in this period
        const statusCount = {};
        period.purchases.forEach(purchase => {
          const status = purchase.status || 'ordered';
          statusCount[status] = (statusCount[status] || 0) + 1;
        });
        const mostCommonStatus = Object.keys(statusCount).reduce((a, b) => 
          statusCount[a] > statusCount[b] ? a : b, 'ordered'
        );
        
        return {
          date: period.period,
          amount: `${Math.round(period.totalAmount).toLocaleString('fr-DZ')} DA`,
          purchases: period.totalPurchases,
          rawAmount: period.totalAmount,
          type: mostCommonType,
          typeDisplay: mostCommonType === 'bon-commande' ? 'Bon de Commande' :
                       mostCommonType === 'bon-reception' ? 'Bon de Réception' :
                       mostCommonType === 'facture' ? 'Facture' :
                       mostCommonType === 'devis' ? 'Devis' :
                       mostCommonType.charAt(0).toUpperCase() + mostCommonType.slice(1),
          status: mostCommonStatus,
          statusDisplay: mostCommonStatus === 'ordered' ? 'Commandé' :
                        mostCommonStatus === 'received' ? 'Reçu' :
                        mostCommonStatus === 'pending' ? 'En Attente' :
                        mostCommonStatus === 'draft' ? 'Brouillon' :
                        mostCommonStatus === 'cancelled' ? 'Annulé' :
                        mostCommonStatus === 'completed' ? 'Complété' :
                        mostCommonStatus.charAt(0).toUpperCase() + mostCommonStatus.slice(1)
        };
      });

    // Supplier purchases using REAL data
    const supplierPurchases = groupPurchasesBySupplier(transformedPurchases)
      .slice(0, 10) // Top 10 suppliers
      .map(supplier => ({
        supplier: supplier.supplier,
        totalAmount: `${Math.round(supplier.totalAmount).toLocaleString('fr-DZ')} DA`,
        purchases: supplier.totalPurchases,
        items: supplier.totalItems,
        rawAmount: supplier.totalAmount
      }));

    // Product purchases using REAL data
    const productPurchases = groupPurchasesByProduct(transformedPurchases, products)
      .slice(0, 10) // Top 10 products
      .map(product => ({
        product: product.product,
        category: product.category,
        quantity: product.quantityPurchased,
        totalAmount: `${Math.round(product.totalAmount).toLocaleString('fr-DZ')} DA`,
        avgPrice: `${Math.round(product.averagePrice).toLocaleString('fr-DZ')} DA`,
        rawAmount: product.totalAmount
      }));

    // Pending orders (orders not yet received)
    const pendingOrders = transformedPurchases
      .filter(purchase => ['ordered', 'pending', 'draft'].includes(purchase.status))
      .slice(0, 10)
      .map(purchase => ({
        date: purchase.date,
        reference: purchase.purchaseNumber,
        supplier: purchase.supplierName,
        amount: purchase.amount,
        status: purchase.status === 'draft' ? 'Brouillon' : 
               purchase.status === 'pending' ? 'En attente' : 
               purchase.status === 'ordered' ? 'Commandé' : purchase.status,
        deliveryDate: purchase.deliveryDate ? new Date(purchase.deliveryDate).toLocaleDateString('fr-FR') : 'N/A'
      }));

    console.log('📈 Purchase data calculated:', {
      periodPurchases: periodPurchases.length,
      supplierPurchases: supplierPurchases.length,
      productPurchases: productPurchases.length,
      pendingOrders: pendingOrders.length,
      samplePeriodPurchase: periodPurchases[0],
      realDataSample: transformedPurchases.slice(0, 2)
    });

    return {
      periodPurchases,
      supplierPurchases,
      productPurchases,
      pendingOrders
    };
  }, [transformedPurchases, products, periodFilter]);

  const { periodPurchases, supplierPurchases, productPurchases, pendingOrders } = purchaseData;
  
  const handleGenerateReport = async () => {
    setIsGenerating(true);
    
    try {
      console.log('🔄 Starting Purchases PDF generation...');
      console.log('📊 Data check:', {
        purchasesCount: transformedPurchases?.length || 0,
        periodPurchases: periodPurchases?.length || 0,
        supplierPurchases: supplierPurchases?.length || 0,
        productPurchases: productPurchases?.length || 0,
        pendingOrders: pendingOrders?.length || 0
      });
      
      // Minimalist red theme colors (EXACTLY like Sales)
      const primaryRed = [185, 28, 28]; // #B91C1C - True red color
      const secondaryRed = [153, 27, 27]; // #991B1B - Darker red
      const lightGray = [248, 248, 248]; // Very light gray for subtle backgrounds
      
      // Create new PDF document
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      let currentPage = 1;
      
      // Helper function to add minimal header and footer (EXACTLY like Sales)
      const addHeaderFooter = async () => {
        // HEADER - Company logo and name (EXACTLY like Sales)
        let headerYPosition = 10;
        
        // Add company logo if available
        if (companySettings?.logo) {
          try {
            // Create image from logo data (base64)
            const logoImg = new Image();
            logoImg.src = companySettings.logo;
            
            // Add logo to PDF (small size: 15mm width, proportional height)
            pdf.addImage(companySettings.logo, 'PNG', 20, 5, 15, 10);
            
            // Position company name next to logo
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(0, 0, 0); // Black color
            const companyName = companySettings?.companyName || 'INVENTAIRE V2';
            pdf.text(companyName, 40, 12);
            
            headerYPosition = 18; // Adjust for logo height
          } catch (error) {
            console.log('Error adding logo to PDF:', error);
            // Fallback to text-only header
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(0, 0, 0); // Black color
            const companyName = companySettings?.companyName || 'INVENTAIRE V2';
            pdf.text(companyName, 20, 10);
          }
        } else {
          // No logo - just company name
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(0, 0, 0); // Black color
          const companyName = companySettings?.companyName || 'INVENTAIRE V2';
          pdf.text(companyName, 20, 10);
        }
        
        // Simple thin line below header
        pdf.setDrawColor(...primaryRed);
        pdf.setLineWidth(0.2);
        pdf.line(20, headerYPosition, pageWidth - 20, headerYPosition);
        
        // FOOTER - Software name and page number
        pdf.setFontSize(7);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(120, 120, 120);
        pdf.text('INVENTAIRE V2', 20, pageHeight - 8);
        
        // Page number
        pdf.text(`${currentPage}`, pageWidth - 25, pageHeight - 8);
      };
      
      // Helper function to add minimal section header (EXACTLY like Sales)
      const addSectionHeader = async (title) => {
        if (yPosition > 250) {
          pdf.addPage();
          currentPage++;
          await addHeaderFooter();
          yPosition = 25; // Adjust for new header size
        }
        
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(0, 0, 0); // Black color
        pdf.text(title, 20, yPosition);
        yPosition += 5;
        
        // Simple clean line
        pdf.setDrawColor(...primaryRed);
        pdf.setLineWidth(0.5);
        pdf.line(20, yPosition, 80, yPosition);
        
        yPosition += 10;
        
        // Reset to black for content
        pdf.setTextColor(0, 0, 0);
      };
      
      // Add header/footer to first page
      await addHeaderFooter();
      let yPosition = 30; // Start below larger header with logo
      
      // 1. MINIMAL TITLE (EXACTLY like Sales)
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0); // Black color
      pdf.text('RAPPORT DES ACHATS', 20, yPosition);
      yPosition += 10;
      
      // Simple date info
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(120, 120, 120);
      pdf.text(`${new Date().toLocaleDateString('fr-FR')} ${new Date().toLocaleTimeString('fr-FR')}`, 20, yPosition);
      yPosition += 15;
      
      // 2. EXECUTIVE SUMMARY (EXACTLY like Sales)
      await addSectionHeader('RÉSUMÉ EXÉCUTIF');
      
      const totalPurchases = transformedPurchases?.length || 0;
      
      // Calculate total amount from purchases data for accuracy
      let totalAmount = 0;
      if (transformedPurchases && transformedPurchases.length > 0) {
        try {
          totalAmount = transformedPurchases.reduce((sum, purchase) => {
            const purchaseAmount = purchase.rawAmount || 0;
            return sum + purchaseAmount;
          }, 0);
          
          console.log('📊 PDF Amount calculation:', {
            totalPurchases,
            totalAmount,
            purchasesWithItems: transformedPurchases.filter(p => p.items && p.items.length > 0).length
          });
        } catch (e) {
          console.log('Amount calculation error:', e);
          totalAmount = 0;
        }
      }
      
      const topSupplier = supplierPurchases[0];
      const topProduct = productPurchases[0];
      
      // Remove decorative boxes - keep it minimal (EXACTLY like Sales)
      const summaryData = [
        ['Nombre total d\'achats', totalPurchases.toString()],
        ['Montant total des achats', `${totalAmount.toLocaleString('fr-FR').replace(/\s/g, ',')} DA`],
        ['Principal fournisseur', topSupplier ? `${topSupplier.supplier || 'N/A'} (${topSupplier.totalAmount || '0 DA'})` : 'Aucune donnée'],
        ['Produit le plus acheté', topProduct ? `${topProduct.product || 'N/A'} (${topProduct.quantity || 0} unités)` : 'Aucune donnée'],
        ['Période d\'analyse', 'Toutes les données disponibles']
      ];
      
      autoTable(pdf, {
        startY: yPosition,
        head: [['Métrique', 'Valeur']],
        body: summaryData,
        theme: 'plain',
        headStyles: { 
          fillColor: [255, 255, 255], 
          textColor: [0, 0, 0], 
          fontSize: 10, 
          fontStyle: 'bold',
          lineWidth: 0.1,
          lineColor: [200, 200, 200]
        },
        bodyStyles: { 
          fontSize: 9, 
          lineWidth: 0.1,
          lineColor: [230, 230, 230]
        },
        columnStyles: {
          0: { cellWidth: 60, fontStyle: 'bold' },
          1: { cellWidth: 80 }
        },
        margin: { left: 20, right: 20 }
      });
      
      yPosition = pdf.lastAutoTable.finalY + 15;
      
      // 3. PURCHASES BY PERIOD
      if (periodPurchases.length > 0) {
        await addSectionHeader('ACHATS PAR PÉRIODE');
        
        const periodTableData = periodPurchases.map(period => [
          period.date,
          period.purchases.toString(),
          period.amount,
          period.typeDisplay
        ]);
        
        autoTable(pdf, {
          startY: yPosition,
          head: [['Période', 'Nombre', 'Montant Total', 'Type Principal']],
          body: periodTableData,
          theme: 'plain',
          headStyles: { 
            fillColor: [255, 255, 255], 
            textColor: [0, 0, 0], 
            fontSize: 10, 
            fontStyle: 'bold',
            lineWidth: 0.1,
            lineColor: [200, 200, 200]
          },
          bodyStyles: { 
            fontSize: 9,
            lineWidth: 0.1,
            lineColor: [230, 230, 230]
          },
          margin: { left: 20, right: 20 }
        });
        
        yPosition = pdf.lastAutoTable.finalY + 15;
      }
      
      // 4. PURCHASES BY SUPPLIER
      if (supplierPurchases.length > 0) {
        await addSectionHeader('ACHATS PAR FOURNISSEUR');
        
        const supplierTableData = supplierPurchases.slice(0, 10).map(supplier => [
          supplier.supplier,
          supplier.purchases.toString(),
          supplier.totalAmount
        ]);
        
        autoTable(pdf, {
          startY: yPosition,
          head: [['Fournisseur', 'Nb Achats', 'Montant Total']],
          body: supplierTableData,
          theme: 'plain',
          headStyles: { 
            fillColor: [255, 255, 255], 
            textColor: [0, 0, 0], 
            fontSize: 10, 
            fontStyle: 'bold',
            lineWidth: 0.1,
            lineColor: [200, 200, 200]
          },
          bodyStyles: { 
            fontSize: 9,
            lineWidth: 0.1,
            lineColor: [230, 230, 230]
          },
          margin: { left: 20, right: 20 }
        });
        
        yPosition = pdf.lastAutoTable.finalY + 15;
      }
      
      // 5. TOP PRODUCTS PURCHASED
      if (productPurchases.length > 0) {
        await addSectionHeader('PRODUITS LES PLUS ACHETÉS');
        
        const productTableData = productPurchases.slice(0, 10).map(product => [
          product.product,
          product.quantity.toString(),
          product.totalAmount
        ]);
        
        autoTable(pdf, {
          startY: yPosition,
          head: [['Produit', 'Quantité', 'Montant Total']],
          body: productTableData,
          theme: 'plain',
          headStyles: { 
            fillColor: [255, 255, 255], 
            textColor: [0, 0, 0], 
            fontSize: 10, 
            fontStyle: 'bold',
            lineWidth: 0.1,
            lineColor: [200, 200, 200]
          },
          bodyStyles: { 
            fontSize: 9,
            lineWidth: 0.1,
            lineColor: [230, 230, 230]
          },
          margin: { left: 20, right: 20 }
        });
        
        yPosition = pdf.lastAutoTable.finalY + 15;
      }
      
      // 6. PENDING ORDERS
      if (pendingOrders.length > 0) {
        await addSectionHeader('COMMANDES EN COURS');
        
        const pendingTableData = pendingOrders.map(order => [
          order.date,
          order.reference,
          order.supplier,
          order.amount,
          order.status
        ]);
        
        autoTable(pdf, {
          startY: yPosition,
          head: [['Date', 'Référence', 'Fournisseur', 'Montant', 'Statut']],
          body: pendingTableData,
          theme: 'plain',
          headStyles: { 
            fillColor: [255, 255, 255], 
            textColor: [0, 0, 0], 
            fontSize: 10, 
            fontStyle: 'bold',
            lineWidth: 0.1,
            lineColor: [200, 200, 200]
          },
          bodyStyles: { 
            fontSize: 9,
            lineWidth: 0.1,
            lineColor: [230, 230, 230]
          },
          margin: { left: 20, right: 20 }
        });
      }
      
      // 7. FINAL PAGE SETUP - Add header/footer to all pages
      const totalPages = pdf.getNumberOfPages();
      for (let i = 2; i <= totalPages; i++) {
        pdf.setPage(i);
        currentPage = i;
        await addHeaderFooter();
      }
      
      // Generate filename and download
      const fileName = `Rapport_Achats_Complet_${new Date().toISOString().slice(0, 10)}.pdf`;
      console.log('💾 Saving PDF:', fileName);
      
      pdf.save(fileName);
      
      setIsGenerating(false);
      toast.success('Rapport PDF des achats généré avec succès!');
      console.log('✅ Purchases PDF generation completed successfully');
      
    } catch (error) {
      console.error('❌ Purchases PDF generation error:', error);
      setIsGenerating(false);
      toast.error(`Erreur lors de la génération du PDF: ${error.message}`);
    }
  };
  
  // Render report content based on REAL DATA
  const renderReportContent = () => {
    switch (selectedReport) {
      case 'purchase-by-period':
        return (
          <div className="overflow-x-auto bg-white/5 border border-white/10 rounded-xl">
            <table className="w-full">
              <thead className="bg-white/10">
                <tr>
                  <th className="px-4 py-3 text-left text-white font-medium">Période</th>
                  <th className="px-4 py-3 text-left text-white font-medium">Nombre d'achats</th>
                  <th className="px-4 py-3 text-left text-white font-medium">Montant total</th>
                  <th className="px-4 py-3 text-left text-white font-medium">Type</th>
                  <th className="px-4 py-3 text-left text-white font-medium">Statut</th>
                </tr>
              </thead>
              <tbody>
                {periodPurchases.length > 0 ? (
                  periodPurchases.map((purchase, index) => (
                    <tr key={index} className="border-t border-white/10">
                                             <td className="px-4 py-3 text-white">{purchase.date}</td>
                       <td className="px-4 py-3 text-white">{purchase.purchases}</td>
                       <td className="px-4 py-3 text-white">{purchase.amount}</td>
                       <td className="px-4 py-3 text-white">{purchase.typeDisplay}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          purchase.status === 'completed' || purchase.status === 'received' ? 'bg-green-500/20 text-green-400' :
                          purchase.status === 'ordered' ? 'bg-blue-500/20 text-blue-400' :
                          purchase.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                          purchase.status === 'draft' ? 'bg-gray-500/20 text-gray-400' :
                          purchase.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                          'bg-blue-500/20 text-blue-400'
                        }`}>
                          {purchase.statusDisplay}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="px-4 py-8 text-center text-white/60">
                      Aucun achat pour cette période
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        );

      case 'purchase-by-supplier':
        return (
          <div className="overflow-x-auto bg-white/5 border border-white/10 rounded-xl">
            <table className="w-full">
              <thead className="bg-white/10">
                <tr>
                  <th className="px-4 py-3 text-left text-white font-medium">Fournisseur</th>
                  <th className="px-4 py-3 text-left text-white font-medium">Nombre d'achats</th>
                  <th className="px-4 py-3 text-left text-white font-medium">Articles</th>
                  <th className="px-4 py-3 text-left text-white font-medium">Montant total</th>
                </tr>
              </thead>
              <tbody>
                {supplierPurchases.length > 0 ? (
                  supplierPurchases.map((supplier, index) => (
                    <tr key={index} className="border-t border-white/10">
                      <td className="px-4 py-3 text-white font-medium">{supplier.supplier}</td>
                      <td className="px-4 py-3 text-white">{supplier.purchases}</td>
                      <td className="px-4 py-3 text-white">{supplier.items}</td>
                      <td className="px-4 py-3 text-white">{supplier.totalAmount}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="px-4 py-8 text-center text-white/60">
                      Aucun achat par fournisseur trouvé
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        );

      case 'purchase-by-product':
        return (
          <div className="overflow-x-auto bg-white/5 border border-white/10 rounded-xl">
            <table className="w-full">
              <thead className="bg-white/10">
                <tr>
                  <th className="px-4 py-3 text-left text-white font-medium">Produit</th>
                  <th className="px-4 py-3 text-left text-white font-medium">Catégorie</th>
                  <th className="px-4 py-3 text-left text-white font-medium">Quantité</th>
                  <th className="px-4 py-3 text-left text-white font-medium">Montant total</th>
                  <th className="px-4 py-3 text-left text-white font-medium">Prix moyen</th>
                </tr>
              </thead>
              <tbody>
                {productPurchases.length > 0 ? (
                  productPurchases.map((product, index) => (
                    <tr key={index} className="border-t border-white/10">
                      <td className="px-4 py-3 text-white font-medium">{product.product}</td>
                      <td className="px-4 py-3 text-white">{product.category}</td>
                      <td className="px-4 py-3 text-white">{product.quantity}</td>
                      <td className="px-4 py-3 text-white">{product.totalAmount}</td>
                      <td className="px-4 py-3 text-white">{product.avgPrice}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="px-4 py-8 text-center text-white/60">
                      Aucun achat par produit trouvé
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={onBack}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <ArrowLeftIcon className="h-6 w-6 text-white" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white">Rapports d'achats</h1>
            <p className="text-white/60">Suivez vos achats et analysez vos fournisseurs</p>
          </div>
        </div>
        
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleGenerateReport}
          disabled={isGenerating}
          className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center space-x-2"
        >
          {isGenerating ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>Génération...</span>
            </>
          ) : (
            <>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span>Exporter PDF Complet</span>
            </>
          )}
        </motion.button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white/5 border border-white/10 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Types de rapports</h2>
            <div className="space-y-2">
              {reportTypes.map((type) => (
                <motion.button
                  key={type.id}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setSelectedReport(type.id)}
                  className={`w-full text-left p-3 rounded-lg transition-colors ${
                    selectedReport === type.id
                      ? 'bg-blue-500/20 border border-blue-500/30 text-blue-400'
                      : 'hover:bg-white/5 text-white/80'
                  }`}
                >
                  <div className="font-medium">{type.name}</div>
                  <div className="text-sm text-white/60">{type.description}</div>
                </motion.button>
              ))}
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className="lg:col-span-3">
          <div className="bg-white/5 border border-white/10 rounded-xl p-6">
            {/* Header with filters */}
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-white">
                {reportTypes.find(type => type.id === selectedReport)?.name}
              </h2>
              
             
            </div>

            {/* Report content */}
            {renderReportContent()}
          </div>

          {/* Pending orders section */}
          <div className="mt-8 bg-white/5 border border-white/10 rounded-xl p-6">
            <h3 className="text-xl font-semibold text-white mb-4">Commandes en cours</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white/10">
                  <tr>
                    <th className="px-4 py-3 text-left text-white font-medium">Date</th>
                    <th className="px-4 py-3 text-left text-white font-medium">Référence</th>
                    <th className="px-4 py-3 text-left text-white font-medium">Fournisseur</th>
                    <th className="px-4 py-3 text-left text-white font-medium">Montant</th>
                    <th className="px-4 py-3 text-left text-white font-medium">Statut</th>
                    <th className="px-4 py-3 text-left text-white font-medium">Livraison prévue</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingOrders.length > 0 ? (
                    pendingOrders.map((order, index) => (
                      <tr key={index} className="border-t border-white/10">
                        <td className="px-4 py-3 text-white">{order.date}</td>
                        <td className="px-4 py-3 text-white">{order.reference}</td>
                        <td className="px-4 py-3 text-white">{order.supplier}</td>
                        <td className="px-4 py-3 text-white">{order.amount}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            order.status === 'Brouillon' ? 'bg-gray-500/20 text-gray-400' :
                            order.status === 'En attente' ? 'bg-yellow-500/20 text-yellow-400' :
                            order.status === 'Commandé' ? 'bg-blue-500/20 text-blue-400' :
                            'bg-green-500/20 text-green-400'
                          }`}>
                            {order.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-white">{order.deliveryDate}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="px-4 py-8 text-center text-white/60">
                        Aucune commande en cours
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PurchasesReports;
